import React from 'react';
import { Dropdown, MenuProps, Typography, message, notification, Modal, Tooltip, Button } from 'antd';
import { DeleteOutlined, EditOutlined, MoreOutlined, CopyOutlined } from '@ant-design/icons';
import styled from 'styled-components/macro';

import { OwnershipTypeEntity } from '../../../../types.generated';
import useDropdownKeyboard from '../../../hooks/useDropdownKeyboard';
import { useDeleteOwnershipTypeMutation } from '../../../../graphql/ownership.generated';

const DROPDOWN_TEST_ID = 'ownership-table-dropdown';
const EDIT_OWNERSHIP_TYPE_TEST_ID = 'edit-ownership-type';
const DELETE_OWNERSHIP_TYPE_TEST_ID = 'delete-ownership-type';

const StyledDropdown = styled(Dropdown)`
    min-width: 50px;
`;

const StyledURNButton = styled(Button)`
    padding: 0;
    display: flex;
    justify-content: center;
`;

const ActionsColumnWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const MenuButtonContainer = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;

    svg {
        font-size: 16px;
    }
`;

const MenuButtonText = styled(Typography.Text)`
    font-size: 14px;
    margin-left: 8px;
`;

const StyledMoreOutlined = styled(MoreOutlined)`
    width: 20px;
    &&& {
        padding-left: 0px;
        padding-right: 0px;
        font-size: 18px;
    }
    :hover {
        cursor: pointer;
    }
`;

type Props = {
    ownershipType: OwnershipTypeEntity;
    setIsOpen: (isOpen: boolean) => void;
    setOwnershipType: (ownershipType: OwnershipTypeEntity) => void;
    refetch: () => void;
};

export const ActionsColumn = ({ ownershipType, setIsOpen, setOwnershipType, refetch }: Props) => {
    const { visible, setVisible, handleKeyDown, toggleDropdown } = useDropdownKeyboard();

    const editOnClick = () => {
        setIsOpen(true);
        setOwnershipType(ownershipType);
    };

    const [deleteOwnershipTypeMutation] = useDeleteOwnershipTypeMutation();

    const onDelete = () => {
        deleteOwnershipTypeMutation({
            variables: {
                urn: ownershipType.urn,
            },
        })
            .then(() => {
                notification.success({
                    message: `Success`,
                    description: 'You have deleted an ownership type.',
                    placement: 'bottomLeft',
                    duration: 3,
                });
                setTimeout(() => {
                    refetch();
                }, 3000);
            })
            .catch((e: unknown) => {
                message.destroy();
                if (e instanceof Error) {
                    message.error({
                        content: `Failed to delete an ownership type`,
                        duration: 3,
                    });
                }
            });
    };

    const onConfirmClose = () => {
        Modal.confirm({
            title: `Delete Ownership type`,
            content: `Are you sure you want to delete this ownership type?`,
            onOk() {
                onDelete();
            },
            onCancel() {},
            className: 'f-borderless-modal',
            okText: 'Delete',
            style: {
                maxWidth: 400,
            },
            okButtonProps: {
                type: undefined,
                danger: true,
            },
            maskClosable: true,
            closable: true,
            centered: true,
        });
    };

    const items: MenuProps['items'] = [
        {
            key: 'edit',
            icon: (
                <MenuButtonContainer data-testid={EDIT_OWNERSHIP_TYPE_TEST_ID} tabIndex={0}>
                    <EditOutlined aria-hidden="true" />
                </MenuButtonContainer>
            ),
            label: <MenuButtonText>Edit Ownership Type</MenuButtonText>,
        },
        {
            key: 'delete',
            icon: (
                <MenuButtonContainer data-testid={DELETE_OWNERSHIP_TYPE_TEST_ID} tabIndex={0}>
                    <DeleteOutlined aria-hidden="true" />
                </MenuButtonContainer>
            ),
            label: <MenuButtonText>Delete Ownership Type</MenuButtonText>,
        },
    ];

    const onClick: MenuProps['onClick'] = (e) => {
        const key = e.key as string;
        if (key === 'edit') {
            editOnClick();
        } else if (key === 'delete') {
            onConfirmClose();
        }
    };

    const menuProps: MenuProps = {
        items,
        onClick,
    };

    return (
        <ActionsColumnWrapper>
            <Tooltip title="Copy URN. An URN uniquely identifies an entity on DataCatalog.">
                <StyledURNButton
                    aria-label="Copy URN"
                    icon={<CopyOutlined aria-hidden="true" />}
                    onClick={() => navigator.clipboard.writeText(ownershipType?.urn)}
                />
            </Tooltip>
            <StyledDropdown
                open={visible}
                menu={menuProps}
                trigger={['click']}
                onOpenChange={(flag) => setVisible(flag)}
            >
                <StyledMoreOutlined
                    tabIndex={0}
                    aria-haspopup="true"
                    aria-label="More options"
                    onClick={toggleDropdown}
                    onKeyDown={handleKeyDown}
                    style={{ display: undefined }}
                    date-testid={DROPDOWN_TEST_ID}
                />
            </StyledDropdown>
        </ActionsColumnWrapper>
    );
};
